import { useQuery } from '@tanstack/react-query';

import { apiPost } from '@/lib/http/client';

export interface EmailPreviewPayload {
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  body: string;
  body_html?: string;
  social_account_id?: number;
  reply_to_message_id?: string;
  thread_id?: string;
  /**
   * The draft's typed signature state. When present the server prefers it
   * over marker-presence inference — required for a 'user_removed' draft
   * (marker gone) to preview WITHOUT the signature the send omits.
   */
  signature_disposition?: 'none' | 'inserted' | 'user_removed';
}

export interface EmailPreviewOriginalMessage {
  sender: string;
  date: string | null;
  subject: string;
  external_link: string;
}

export interface EmailPreviewResponse {
  subject: string;
  body: string;
  body_html: string | null;
  signature_applied: boolean;
  signature_status?:
    | 'applied'
    | 'disabled'
    | 'no_signature'
    | 'already_in_draft';
  footer_applied: boolean;
  is_reply: boolean;
  account_email: string;
  provider: string;
  original_message: EmailPreviewOriginalMessage | null;
}

export function useEmailPreview(
  orgId: number,
  payload: EmailPreviewPayload,
  options: { enabled: boolean }
) {
  return useQuery({
    // TanStack Query hashes queryKey by content (stable JSON.stringify, not
    // object reference), so recreating `payload` on every handlePreview call
    // does not cause spurious refetches or cross-payload data leakage.
    queryKey: ['email-preview', orgId, payload],
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      // timeout: 8000ms, within the client's documented 5s-10s interactive-feature range
      apiPost<EmailPreviewResponse>(
        `/api/orgs/${orgId}/email/preview/`,
        payload,
        8000,
        { signal }
      ),
    enabled: options.enabled,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}
